import React, { useContext, useCallback } from 'react';
import { compose } from 'recompose';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { TextField, Grid, Accordion, AccordionDetails, AccordionSummary, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
import { OnwardBreadcrumbActive, OnwardBreadcrumbInactive, OnwardBreadcrumbSpacer } from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import { PrimaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Container, Card, H1, H2, H3, Body1 } from './blocks';
import { Context, ContextProvider } from './store';
import Header from './Header';
import Charge from './Charge';
import OrderInfoSection from './OrderInfoSection';
import { isFinite } from 'lodash';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import { withWarehouseEstimateEvent } from '@/utilities/warehouseEvents';

const Accessorials = () => {
    const { state, loading, callbacks } = useContext(Context);
    const navigate = useNavigate();
    const { editingOrder } = state;
    const { setEditingOrder, upsertOrder } = callbacks;

    const onSaveOrder = useCallback(
        ({
            errors,
            order: modified,
            items: itemsModified,
            itemsRemoved,
            itemUpdates,
            tags,
            tagsRemoved,
            documents,
            orderIds,
            subscriptions,
            services,
            servicesRemoved,
        }) => {
            const { notes, ...rest } = modified;

            upsertOrder({
                variables: {
                    orders: [
                        {
                            ...withWarehouseEstimateEvent(rest),
                            notes: {
                                data: notes,
                                on_conflict: {
                                    constraint: 'notes_pkey',
                                    update_columns: ['note'],
                                },
                            },
                        },
                    ],
                    items: itemsModified || [],
                    removals: itemsRemoved || [],
                    item_updates: itemUpdates || [],
                    tags: tags,
                    tag_removals: tagsRemoved,
                    order_ids: orderIds,
                    documents: documents || [],
                    email_subscriptions: subscriptions || [],
                    services: services || [],
                    service_removals: servicesRemoved || [],
                },
            });

            setEditingOrder(null);
        },
        [upsertOrder]
    );

    if (loading.init || !state.updated?.order_id) return null;

    return (
        <>
            <EditOrderModal
                order={editingOrder}
                open={!!editingOrder}
                callbacks={{
                    onSave: onSaveOrder,
                    onClose: () => setEditingOrder(null),
                    onError: (error) => {
                        callbacks.setNotification({
                            message: `We're sorry, something went wrong.`,
                            type: 'error',
                        });
                    },
                }}
            />
            <Grid
                direction="row"
                container
                css={css`
                    align-items: center;
                    background: white;
                    padding: 12px 30px;
                    box-shadow: 0px 0px 0px 1px rgba(63, 63, 68, 0.05), 0px 1px 3px rgba(63, 63, 68, 0.15);
                    border-bottom: 1px solid #ececec;
                `}
            >
                <Grid
                    direction="column"
                    container
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                    `}
                >
                    <Grid
                        direction="row"
                        container
                        css={css`
                            align-items: center;
                        `}
                    >
                        <OnwardBreadcrumbActive onClick={() => navigate(-1)}>Completed Orders</OnwardBreadcrumbActive>
                        <OnwardBreadcrumbSpacer />
                        <OnwardBreadcrumbInactive disabled>{state.updated.order_number}</OnwardBreadcrumbInactive>
                    </Grid>
                </Grid>
                {state.editable && !state.isInvoiced && (
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            align-items: flex-end;
                        `}
                    >
                        <PrimaryButton
                            css={css`
                                height: 40px;
                                align-self: flex-end;
                                justify-content: center;
                                text-align: center;
                            `}
                            onClick={callbacks.readyToInvoice}
                            disabled={
                                state.updated.invoicing_status === 'APPROVED' ||
                                loading.update ||
                                state.hasCommentErrors
                            }
                        >
                            {state.updated.invoicing_status === 'APPROVED' ? 'Approved' : 'Approve for Invoicing'}
                        </PrimaryButton>
                    </Grid>
                )}
            </Grid>

            <Container>
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 35px;
                    `}
                >
                    <div
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            width: 100%;
                        `}
                    >
                        <H1>
                            {['order_number', 'po_number', 'reference_id']
                                .map((attr) => state.updated[attr])
                                .join(' / ')}
                        </H1>
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                            `}
                        >
                            <IconButton
                                onClick={() => setEditingOrder(state.updated)}
                                disabled={!state.editable || loading.update}
                                css={css`
                                    border-radius: 10%;
                                    background-color: white;
                                    &:hover {
                                        background-color: ${state.editable ? 'lightgrey' : 'white'};
                                    }
                                    transform: scale(0.65);
                                `}
                            >
                                <EditIcon
                                    style={{ color: state.editable ? '59b863' : '#bdbdbd', backgroundColor: 'white' }}
                                />
                            </IconButton>
                            <IconButton
                                onClick={() => navigate(`/order/${state.updated.order_id}`, { target: '_blank' })}
                                css={css`
                                    border-radius: 10%;
                                    background-color: white;
                                    &:hover {
                                        background-color: lightgrey;
                                    }
                                    transform: scale(0.65);
                                `}
                            >
                                <OpenInNewIcon style={{ color: '59b863', backgroundColor: 'white' }} />
                            </IconButton>
                        </div>
                    </div>
                </Grid>

                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-bottom: 20px;
                    `}
                >
                    <Accordion
                        css={css`
                            width: 100%;
                        `}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <H2>Billing Comments ({state.invoicingNotes?.length || 0})</H2>
                        </AccordionSummary>
                        <AccordionDetails
                            css={css`
                                display: flex;
                                flex-direction: column;
                                padding: 0 1rem 0rem 1rem;
                                margin-bottom: 0px;
                                justify-content: space-between;
                            `}
                        >
                            {state.invoicingNotes?.map((note, i) => (
                                <Grid
                                    container
                                    direction="row"
                                    key={i}
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        padding: '10px',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            width: 100%;
                                        `}
                                    >
                                        <Grid
                                            container
                                            direction="row"
                                            css={css`
                                                margin-bottom: 8px;
                                            `}
                                        >
                                            <Body1
                                                css={css`
                                                    font-size: 12px;
                                                    color: #4c4c4c;
                                                `}
                                            >
                                                {note.user?.username} - {new Date(note.created_at).toLocaleString()}
                                            </Body1>
                                        </Grid>
                                        <Grid container direction="row">
                                            <Body1>{note.note}</Body1>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ))}
                            <TextField
                                multiline
                                charLimit={200}
                                maxRows={2}
                                value={state.newNote || ''}
                                css={css`
                                    width: 100%;
                                    height: 100px;
                                    justify-content: center;
                                `}
                                variant="outlined"
                                placeholder="Add a comment..."
                                onChange={(e) => {
                                    callbacks.setNewNote(e.target.value);
                                }}
                            />
                            <PrimaryButton
                                css={css`
                                    margin-bottom: 10px;
                                    height: 40px;
                                    align-self: flex-end;
                                    justify-content: center;
                                    text-align: center;
                                `}
                                onClick={() => {
                                    callbacks.saveNote();
                                }}
                            >
                                Submit
                            </PrimaryButton>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid direction="row" container>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 2;
                            flex-basis: 0;
                            margin-right: 10px;
                        `}
                    >
                        <Card>
                            <Header />
                            <Charge
                                charge={state.baseCharge}
                                editable={state.editable}
                                qbo_tags={state.qbo_tags}
                                isBaseCharge
                                isEdited={state.isBaseChargeEdited}
                                callbacks={{
                                    onChange: (next) => {
                                        callbacks.editBaseCharge(next);
                                    },
                                    onCommentChange: (comment) => {
                                        callbacks.editBaseChargeComment(comment);
                                    },
                                }}
                            />

                            {(state.accessorials || []).map((charge, idx) => {
                                return (
                                    <Charge
                                        key={idx}
                                        charge={charge}
                                        options={state.options}
                                        rates={state.rates}
                                        editable={state.editable}
                                        qbo_tags={state.qboTags}
                                        isEdited={state.isAccessorialsEdited}
                                        callbacks={{
                                            onChange: (next) => {
                                                callbacks.editCharge({ idx, charge: next });
                                            },
                                            onRemove: () => {
                                                callbacks.removeCharge({ idx });
                                            },
                                        }}
                                    />
                                );
                            })}

                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-top: 20px;
                                `}
                            >
                                <Autocomplete
                                    size="small"
                                    options={state.options}
                                    getOptionLabel={(option) =>
                                        option?.qbo_tag ? `[${option.qbo_tag}]: ${option.label}` : option?.label || ''
                                    }
                                    value={state.newCharge || null}
                                    onChange={(_, value) => {
                                        callbacks.setNewCharge(value);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            variant="outlined"
                                            placeholder="Select Charge..."
                                        />
                                    )}
                                    css={css`
                                        width: 200px;
                                        margin-right: 24px;
                                    `}
                                />
                                <PrimaryButton
                                    disabled={!state.editable || !state.newCharge?.type}
                                    onClick={callbacks.addCharge}
                                >
                                    Add Charge
                                </PrimaryButton>
                            </Grid>

                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-top: 45px;
                                    margin-bottom: 8px;
                                    padding: 0px 25px;
                                `}
                            >
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                    `}
                                >
                                    <Body1>Subtotal</Body1>
                                </Grid>
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        align-items: flex-end;
                                    `}
                                >
                                    <H3>{isFinite(state.subtotal) ? state.subtotal.toFixed(2) : '--'}</H3>
                                </Grid>
                            </Grid>

                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-bottom: 20px;
                                    padding: 0px 25px;
                                    padding-bottom: 20px;

                                    border-bottom: 1px solid #4c4c4c;
                                `}
                            >
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                    `}
                                >
                                    <Body1>Accessorials</Body1>
                                </Grid>
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                        align-items: flex-end;
                                    `}
                                >
                                    <H3
                                        css={css`
                                            color: ${colors.greens.primary};
                                        `}
                                    >
                                        {isFinite(state.accessorialsSum) ? state.accessorialsSum.toFixed(2) : '--'}
                                    </H3>
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                css={css`
                                    margin-bottom: 44px;
                                `}
                            >
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                    `}
                                />
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        flex: 1;
                                        flex-basis: 0;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="row"
                                        css={css`
                                            padding: 0 25px;
                                        `}
                                    >
                                        <Grid
                                            container
                                            direction="column"
                                            css={css`
                                                flex: 1;
                                                flex-basis: 0;
                                                justify-content: center;
                                            `}
                                        >
                                            <Body1>Total</Body1>
                                        </Grid>
                                        <Grid
                                            container
                                            direction="column"
                                            css={css`
                                                flex: 1;
                                                flex-basis: 0;
                                                justify-content: center;
                                                align-items: flex-end;
                                            `}
                                        >
                                            <H3>{isFinite(state.total) ? state.total.toFixed(2) : '--'}</H3>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                css={css`
                                    justify-content: flex-end;
                                `}
                            >
                                <PrimaryButton
                                    css={css`
                                        height: 40px;
                                        align-self: flex-end;
                                        justify-content: center;
                                        text-align: center;
                                    `}
                                    onClick={callbacks.saveOrder}
                                    disabled={loading.update || !state.editable}
                                >
                                    Save
                                </PrimaryButton>
                            </Grid>
                        </Card>
                    </Grid>
                    <Grid
                        direction="column"
                        container
                        css={css`
                            flex: 1;
                            flex-basis: 0;
                            margin-left: 10px;
                            margin-bottom: 4rem;
                            width: 40%;
                        `}
                    >
                        <OrderInfoSection />
                    </Grid>
                </Grid>
            </Container>

            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={callbacks.clearNotification}
            />
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(Accessorials);
